import { useForm } from 'react-hook-form';
import { Notice } from '../../../components/ui/notice.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmEventDetails } from '../../../server/types/infer-trpc-types.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import { useUpdateEvent } from '../hooks/use-update-event.ts';
import { getInputFromCrmEventDetails } from '../utils/get-input-from-crm-event-details.ts';
import type { EventFormData } from './event-form-fields.tsx';
import { EventFormFields } from './event-form-fields.tsx';

export type EventEditDialogProps = {
    eventId: string;
    eventDetails: CrmEventDetails;
    onUpdated?: () => void;
};

export const EventEditDialog = DialogStateManager.create<EventEditDialogProps>(
    ({ eventId, eventDetails, onUpdated }) => {
        const { t } = useLocale('events');
        const { toast } = useToast();
        const { visible, remove } = useDialog();
        const sendEvent = useAnalytics();

        const originalValues = getInputFromCrmEventDetails(eventDetails);

        const updateEventMutation = useUpdateEvent({
            onSuccess: (_, { data }) => {
                sendEvent('event_updated', {
                    is_recurring: !!data.recurrence,
                    has_set_time: !!data.start_time && !!data.end_time,
                    has_customer: !!data.customer_id,
                    address_changed: data.address !== originalValues.address,
                    invitees_added: !!data.attendees && data.attendees.length > 0,
                    has_agenda: !!data.agenda,
                });
                remove();
                onUpdated?.();
            },
            onError: (message) => {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: message ?? 'An error occurred',
                });
            },
        });

        const form = useForm<EventFormData>({
            defaultValues: originalValues,
            mode: 'onBlur',
        });

        const { handleSubmit } = form;

        const onSubmit = (data: EventFormData) => {
            updateEventMutation.mutate({
                eventId,
                data,
            });
        };

        return (
            <Dialog open={visible} onClose={remove} showClose>
                <DialogForm className="sm:max-w-2xl">
                    <DialogForm.Header title={t('events__form_edit_title')} />
                    <DialogForm.Content>
                        {!!form.watch('recurrence') && (
                            <Notice
                                variant="info"
                                title={t('events__recurrence_notice_title')}
                                description={t('events__recurrence_notice_description')}
                            />
                        )}
                        <EventFormFields
                            form={form}
                            customer={eventDetails.customer}
                            ownerName={eventDetails.owner_name ?? ''}
                        />
                    </DialogForm.Content>
                    <DialogForm.SubmitBlock
                        onCancel={remove}
                        onSubmit={handleSubmit(onSubmit)}
                        disabled={updateEventMutation.isLoading}
                    />
                </DialogForm>
            </Dialog>
        );
    },
);
